import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Link as RouterLink } from 'react-router-dom';
import { ClientPicker } from './components/ClientPicker';
import { ManualClientForm } from './components/ManualClientForm';
import { LactateTestForm } from './components/LactateTestForm';
import { LactateClient } from './types';
import { directoryToLactateClient } from './utils/manualClient';
import { useAuth } from '../auth/useAuth';

const WHAT_YOU_NEED = [
  'Threshold 1 & 2 values - intensity, heart rate, % HRmax',
  'Training-zone boundaries (Z1 through Z6/7)',
  'Measured HRmax, plus VO₂max data if it was captured',
  'A lactate curve chart image (PNG / JPG / SVG)',
];

type Mode = 'search' | 'manual';

const LactateThresholdPage: React.FC = () => {
  const { user } = useAuth();
  const [client, setClient] = useState<LactateClient | null>(null);
  const [mode, setMode] = useState<Mode>('search');

  if (client) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setClient(null)} sx={{ mb: 2 }}>
          Choose a different client
        </Button>
        <LactateTestForm client={client} createdBy={user?.uid ?? ''} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        Lactate Threshold Testing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
        Turn a client&rsquo;s lactate test into a branded Initial or Review report - thresholds,
        training zones, and VO₂max, rendered to a shareable PDF.
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, maxWidth: 720 }}>
        {mode === 'search' ? (
          <>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Start a new report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Search for the client you tested to begin.
            </Typography>
            <ClientPicker onSelect={(c) => setClient(directoryToLactateClient(c))} />

            <Divider sx={{ my: 3 }}>or</Divider>

            <Button
              variant="outlined"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() => setMode('manual')}
            >
              Add a client who isn&rsquo;t in the system yet
            </Button>

            <Typography variant="subtitle2" sx={{ mt: 3.5, mb: 1 }}>
              What you&rsquo;ll need
            </Typography>
            <List dense disablePadding>
              {WHAT_YOU_NEED.map((item) => (
                <ListItem key={item} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 30, mt: 0.25, color: 'success.main' }}>
                    <CheckCircleOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setMode('search')}
              sx={{ mb: 2, ml: -1 }}
            >
              Back to search
            </Button>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              New client details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              For a client who isn&rsquo;t in the database yet. Their Mindbody ID keys the report so
              it links up once they&rsquo;re imported.
            </Typography>
            <ManualClientForm onSubmit={setClient} />
          </>
        )}
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5 }}>
        Looking for a test you already ran?{' '}
        <Link component={RouterLink} to="/reports" underline="hover">
          View past reports
        </Link>
      </Typography>
    </Box>
  );
};

export default LactateThresholdPage;
